import React, { useContext, useEffect, useState, useRef } from 'react';
import { firestore } from '../Firebase';
import { useParams } from 'react-router-dom';
import { serverTimestamp } from 'firebase/firestore';
import { AuthContextPublic } from '../context/AuthContextPublic';

const ChatAdmin = () => {
  const { userName } = useContext(AuthContextPublic) || {};

  const [chatMessages, setChatMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const { videoID: paramVideoID } = useParams();
  const messagesEndRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  const scrollToBottom = () => {
    const container = document.querySelector('.messages-window');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    if (shouldScroll) {
      scrollToBottom();
      setShouldScroll(false);
    }
  }, [chatMessages, shouldScroll]);

  useEffect(() => {
    const unsubscribe = firestore
      .collection('videos')
      .doc(paramVideoID)
      .collection('messages')
      .orderBy('timestamp', 'asc')
      .onSnapshot((querySnapshot) => {
        setChatMessages(
          querySnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }))
        );
        setShouldScroll(true);
      });
    return () => unsubscribe();
  }, [paramVideoID]);

  const handleSendMessage = async () => {
    if (!messageInput.trim()) return;

    try {
      setShouldScroll(true);
      await firestore
        .collection('videos')
        .doc(paramVideoID)
        .collection('messages')
        .add({
          text: messageInput,
          name: userName || 'Anonymous',
          timestamp: serverTimestamp(),
          visible: 1,
        });

      setMessageInput('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleToggleVisibility = async (messageId, currentVisibility) => {
    try {
      await firestore
        .collection('videos')
        .doc(paramVideoID)
        .collection('messages')
        .doc(messageId)
        .set({
          visible: currentVisibility === 1 ? 0 : 1,
        }, { merge: true });
    } catch (error) {
      console.error('Error toggling message visibility:', error);
    }
  };

  return (
    <div className="chat-container" style={styles.container}>
      <h5 className="ps-2">Live Chat</h5>
      <div className="messages-window" style={styles.messagesWindow}>
        {chatMessages.map((message, index) => (
          <div
            key={index}
            style={{
              ...styles.message,
              backgroundColor: message.visible === 1 ? '#fff' : '#e9ecef',
            }}
          >
            <div style={styles.messageContent}>
              <span style={styles.messageName}>{message.name}: </span>
              <span style={styles.messageText}>{message.text}</span>
              <button
                onClick={() =>
                  handleToggleVisibility(message.id, message.visible)
                }
                style={
                  message.visible === 1 ? styles.hideButton : styles.showButton
                }
              >
                {message.visible === 1 ? 'Hide' : 'Show'}
              </button>
            </div>
            <div style={styles.messageTimestamp}>
              {message.timestamp?.toDate().toLocaleTimeString()}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="input-area" style={styles.inputArea}>
        <div style={styles.inputWrapper}>
          <textarea
            placeholder="Type a message..."
            style={styles.input}
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
          <button style={styles.button} onClick={handleSendMessage}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '500px',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  messagesWindow: {
    flex: 1,
    padding: '5px',
    overflowY: 'auto',
    backgroundColor: '#f5f5f5',
  },
  inputArea: {
    padding: '4px 5px 1px 5px',
    backgroundColor: '#fff',
    width: '100%',
    boxSizing: 'border-box',
  },
  inputWrapper: {
    display: 'flex',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  input: {
    flex: 1,
    padding: '3px 6px',
    borderRadius: '4px 0 0 4px',
    border: '1px solid #ccc',
    borderRight: 'none',
    resize: 'vertical',
    minHeight: '60px',
    maxHeight: '150px',
    boxSizing: 'border-box',
    width: '0',
    fontSize: '13px',
  },
  button: {
    padding: '8px',
    backgroundColor: '#0d6efd',
    color: '#fff',
    border: 'none',
    borderRadius: '0 4px 4px 0',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontSize: '13px',
  },
  message: {
    marginBottom: '4px',
    padding: '6px 10px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  messageContent: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  messageText: {
    fontSize: '13px',
  },
  messageTimestamp: {
    fontSize: '11px',
    color: '#666',
    marginTop: '2px',
  },
  messageName: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  hideButton: {
    marginLeft: 'auto',
    padding: '2px 6px',
    fontSize: '11px',
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  showButton: {
    marginLeft: 'auto',
    padding: '2px 6px',
    fontSize: '11px',
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};

export default ChatAdmin;
